import { useState, useMemo, useEffect, useRef } from "react";

export interface UseMobilePaginationResult<T> {
    currentItems: T[];
    page: number;
    totalPages: number;
    canGoNext: boolean;
    canGoPrev: boolean;
    goToNext: () => void;
    goToPrev: () => void;
    startIndex: number;
    endIndex: number;
    total: number;
}

/**
 * Provides client-side pagination for a flat array.
 * Automatically resets to page 0 whenever the array length changes
 * (e.g. after a search / filter update).
 */
export function useMobilePagination<T>(
    items: T[],
    pageSize: number = 10,
    initialPage: number = 0,
): UseMobilePaginationResult<T> {
    const [page, setPage] = useState(initialPage);

    // Reset to first page when the result set size changes (e.g. new search)
    // — but not on mount, so a restored initialPage (e.g. from a URL) sticks.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setPage(0);
    }, [items.length]);

    const total = items.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);

    const currentItems = useMemo(
        () => items.slice(startIndex, endIndex),
        [items, startIndex, endIndex],
    );

    const goToNext = () => setPage((p) => Math.min(p + 1, totalPages - 1));
    const goToPrev = () => setPage((p) => Math.max(p - 1, 0));

    return {
        currentItems,
        page,
        totalPages,
        canGoNext: page < totalPages - 1,
        canGoPrev: page > 0,
        goToNext,
        goToPrev,
        startIndex,
        endIndex,
        total,
    };
}
